import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import Monnify from "monnify-js";
import * as Yup from "yup";

import { initiatePayment, verifyPayment } from "src/api/payments";
import { NumberField } from "src/components/form";
import { Button } from "src/components/ui/button";
import env from "src/config/env";
import { useAuth } from "src/providers/AuthProvider";

const monnify = new Monnify(env.REACT_APP_MONNIFY_API_KEY, env.REACT_APP_MONNIFY_CONTRACT_CODE);

const MINIMUM_TOP_UP = 1000;

const validationSchema = Yup.object().shape({
	amount: Yup.number().min(MINIMUM_TOP_UP, `Amount must be at least ₦${MINIMUM_TOP_UP.toLocaleString()}`).required("Amount is required"),
});

interface TopUpFormValues {
	amount: number | string;
}

const initialValues: TopUpFormValues = { amount: "" };

/** Gateway top-up. Monnify's checkout opens over the modal; success refetches the balance. */
const TopUpWallet = () => {
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const { mutate: verifyMutate, isLoading: isVerifying } = useMutation(verifyPayment, {
		onSuccess: () => {
			queryClient.refetchQueries({ queryKey: ["wallet-balance"] });
		},
	});

	const { mutate: initiateMutate, isLoading: isInitiating } = useMutation(initiatePayment, {
		onSuccess: (payload) => {
			monnify.initializePayment({
				...payload,
				onClose: (response: { status?: string; paymentReference?: string }) => {
					if (response.status === "SUCCESS" && response.paymentReference) {
						queryClient.refetchQueries({ queryKey: ["wallet-balance"] });
						verifyMutate({ reference: response.paymentReference, business_uuid: user?.business_uuid });
					}
				},
			});
		},
	});

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={({ amount }) =>
				initiateMutate({
					transaction_type: "wallet_funding",
					business_uuid: user?.business_uuid,
					amount,
				})
			}
		>
			{({ values, setFieldValue, errors, touched, handleSubmit }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<NumberField
						label="Amount to top up"
						name="amount"
						placeholder="0.00"
						prefix="₦ "
						value={values.amount}
						error={touched.amount && errors.amount}
						onChange={(value) => setFieldValue("amount", value)}
						hint={`Minimum ₦${MINIMUM_TOP_UP.toLocaleString()}`}
						required
					/>

					<Button type="submit" variant="secondary" className="tw-w-full" disabled={isVerifying || isInitiating}>
						{isInitiating || isVerifying ? "Please wait..." : "Top up"}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default TopUpWallet;
